/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Collapse } from 'react-bootstrap'
import { useServiceContext } from '../../../root'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect'
import { folderTypesApiService } from '../../../services/api/api.foldertypes'
import { useRcmTableData } from '../../../hooks/useRcmTableData'

interface FilterFormValues {
  types: any[]
}

const persistenceKey = 'settingsFolderTypesFilter'

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const getDefaultFilter = (): FilterFormValues => ({
    types: []
  })

  const [formValues, setFormValues] = useState<FilterFormValues>(getDefaultFilter())

  useEffect(() => {
    // Load persisted filter on mount
    const persistedFilter = context.service.persist.getGlobal<FilterFormValues>(persistenceKey)
    if (persistedFilter && persistedFilter.types && persistedFilter.types.length > 0) {
      setFormValues(persistedFilter)
      tableData.applyFilter(persistedFilter)
    }
  }, [])

  const applyFilter = (filter: FilterFormValues) => {
    context.service.persist.setGlobal(persistenceKey, filter)
    tableData.applyFilter(filter)
  }

  const handleApplyFilter = () => {
    applyFilter(formValues)
  }

  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()
    setFormValues(defaultFilter)
    applyFilter(defaultFilter)
  }

  const isFilterDefault = (filter: FilterFormValues = formValues) => {
    const defaultFilter = getDefaultFilter()
    return JSON.stringify(filter) === JSON.stringify(defaultFilter)
  }

  const updateField = (field: keyof FilterFormValues, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <div className="filter-wrapper">
          <div className="row">
            <div className="col-sm-6 col-lg-4">
              <div className="form-group" id="afff21ff-aac3-41ad-b20b-ae9d3be44fec">
                <label className="control-label">{translate('FOLDER_TYPES_TITLE')}</label>
                <InfiniteScrollSelect
                  placeholder={translate('SELECT_FOLDER_TYPES...')}
                  name="types"
                  value={formValues.types}
                  asyncLoadFn={async (search: string, page: number) => {
                    try {
                      const response: any = await folderTypesApiService.getOptionsForAsyncPicker(search, page)
                      const options =
                        response.elements?.map((item: any) => ({
                          value: String(item.id),
                          label: item.name
                        })) || []
                      const hasMore = response.elements?.length > 0 && (page + 1) * 25 < response.totalElements
                      return {
                        options,
                        hasMore
                      }
                    } catch (error) {
                      console.error('Error loading folder types for filter:', error)
                      return {
                        options: [],
                        hasMore: false
                      }
                    }
                  }}
                  onChange={(option: any) => {
                    updateField('types', option ? option : [])
                  }}
                  multi={true}
                  searchable
                  id="afff21ff-aac3-41ad-b20b-ae9d3be44fec"
                />
              </div>
            </div>
          </div>
          <div>
            <button
              data-action="filter"
              className="btn btn-primary"
              onClick={handleApplyFilter}
              id="c5b5c162-c198-4fda-ae58-654613b6bd6a"
            >
              {translate('BTN_FILTER')}
            </button>
            <button
              data-action="reset"
              disabled={isFilterDefault()}
              className="btn btn-chita btn-link"
              onClick={handleClearFilter}
              id="66eebb65-64d7-436f-950f-d732ccb32a96"
            >
              {translate('BTN_CLEAR_FILTER')}
            </button>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
